import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { FileText, Download, Loader2, CheckCircle2, Building2, Calendar, Shield, Zap, BarChart3, Receipt, ClipboardList, TrendingUp } from 'lucide-react';
import { Document, Packer, Paragraph, TextRun, Table, TableRow, TableCell, WidthType, BorderStyle, HeadingLevel, AlignmentType } from 'docx';
import { saveAs } from 'file-saver';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { PageHeader } from '@/components/shared/PageHeader';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import apiClient from '@/services/api/client';

function fmtCurrency(v: number): string {
  if (v >= 0) return `$${v.toLocaleString()}`;
  return `-$${Math.abs(v).toLocaleString()}`;
}

// ==================== Word Document Builders ====================

function createHeader(title: string, clientName: string, subtitle: string): Paragraph[] {
  return [
    new Paragraph({ children: [new TextRun({ text: title, bold: true, size: 36, color: '2563eb' })], heading: HeadingLevel.HEADING_1, spacing: { after: 50 } }),
    new Paragraph({ children: [new TextRun({ text: clientName, bold: true, size: 28 })], spacing: { after: 50 } }),
    new Paragraph({ children: [new TextRun({ text: subtitle, size: 20, color: '666666', italics: true })], spacing: { after: 100 } }),
    new Paragraph({ children: [new TextRun({ text: '', size: 10 })], border: { bottom: { style: BorderStyle.SINGLE, size: 6, color: '2563eb' } }, spacing: { after: 300 } }),
  ];
}

function createSectionHeading(text: string): Paragraph {
  return new Paragraph({ children: [new TextRun({ text, bold: true, size: 26, color: '1a1a1a' })], heading: HeadingLevel.HEADING_2, spacing: { before: 300, after: 100 } });
}

function createBullet(text: string, bold = false): Paragraph {
  return new Paragraph({ children: [new TextRun({ text, bold, size: 22 })], bullet: { level: 0 }, spacing: { after: 50 } });
}

function createText(text: string): Paragraph {
  return new Paragraph({ children: [new TextRun({ text, size: 22 })], spacing: { after: 80 } });
}

function createKeyValue(label: string, value: string): Paragraph {
  return new Paragraph({ children: [new TextRun({ text: `${label}: `, size: 22 }), new TextRun({ text: value, bold: true, size: 22 })], spacing: { after: 50 } });
}

function createTable(headers: string[], rows: string[][]): Table {
  const headerRow = new TableRow({
    children: headers.map(h => new TableCell({
      children: [new Paragraph({ children: [new TextRun({ text: h, bold: true, size: 20, color: 'FFFFFF' })], alignment: AlignmentType.CENTER, spacing: { before: 40, after: 40 } })],
      width: { size: Math.floor(9000 / headers.length), type: WidthType.DXA },
      shading: { fill: '2563eb' },
      borders: { top: { style: BorderStyle.SINGLE, size: 1, color: 'cccccc' }, bottom: { style: BorderStyle.SINGLE, size: 1, color: 'cccccc' }, left: { style: BorderStyle.SINGLE, size: 1, color: 'cccccc' }, right: { style: BorderStyle.SINGLE, size: 1, color: 'cccccc' } },
    })),
  });

  const dataRows = rows.map((row, rowIdx) => new TableRow({
    children: row.map(cell => new TableCell({
      children: [new Paragraph({ children: [new TextRun({ text: cell.replace(/\*\*/g, ''), size: 20 })], spacing: { before: 30, after: 30 } })],
      width: { size: Math.floor(9000 / headers.length), type: WidthType.DXA },
      shading: rowIdx % 2 === 0 ? { fill: 'F8FAFC' } : undefined,
      borders: { top: { style: BorderStyle.SINGLE, size: 1, color: 'dddddd' }, bottom: { style: BorderStyle.SINGLE, size: 1, color: 'dddddd' }, left: { style: BorderStyle.SINGLE, size: 1, color: 'dddddd' }, right: { style: BorderStyle.SINGLE, size: 1, color: 'dddddd' } },
    })),
  }));

  return new Table({ rows: [headerRow, ...dataRows], width: { size: 9000, type: WidthType.DXA } });
}

function createFooter(): Paragraph {
  return new Paragraph({
    children: [new TextRun({ text: 'Generated by RoniDoc AI — Confidential', size: 16, color: '999999', italics: true })],
    alignment: AlignmentType.CENTER, spacing: { before: 400 },
  });
}

// ==================== Report Types ====================

const REPORT_TYPES = [
  { id: 'monthly', label: 'Monthly Management Report', icon: BarChart3, description: 'P&L summary, key metrics, expense breakdown, benchmark comparison' },
  { id: 'quarterly', label: 'Quarterly Review', icon: TrendingUp, description: 'Quarter-over-quarter comparison, trend analysis, key ratios' },
  { id: 'yearly', label: 'Year-End Summary', icon: ClipboardList, description: 'Full annual P&L, Balance Sheet summary, year-over-year comparison' },
  { id: 'bas', label: 'BAS Reconciliation Report', icon: Receipt, description: 'GST summary, estimated BAS labels, validation checks' },
  { id: 'health', label: 'Client Health Report', icon: CheckCircle2, description: 'Health score breakdown, compliance status, opportunities' },
  { id: 'compliance', label: 'Compliance Risk Report', icon: Shield, description: 'Audit risk, benchmark compliance, Div 7A status, deadlines' },
  { id: 'tax_planning', label: 'Year-End Tax Planning', icon: Calendar, description: 'Action items with deadlines, estimated savings, ATO rules' },
  { id: 'opportunity', label: 'Tax Opportunity Report', icon: Zap, description: 'All detected opportunities with dollar estimates' },
];

// ==================== Report Generators ====================

async function generateMonthlyReport(clientId: string, clientName: string): Promise<Document> {
  const [dashRes, compRes] = await Promise.all([
    apiClient.get('/firm/dashboard'),
    apiClient.get(`/compliance/check/${clientId}`).catch(() => null),
  ]);

  const clients = dashRes.data.data?.clients || [];
  const client = clients.find((c: any) => c.id === clientId);
  const comp = compRes?.data?.data;

  const children: any[] = [
    ...createHeader('Monthly Management Report', clientName, `Generated: ${new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })}`),
  ];

  // Financial Summary
  if (comp?.xeroData) {
    const x = comp.xeroData;
    children.push(createSectionHeading('Financial Summary'));
    children.push(createTable(
      ['Metric', 'Amount'],
      [
        ['Revenue (Turnover)', fmtCurrency(x.turnover)],
        ['Total Expenses', fmtCurrency(x.totalExpenses)],
        ['Cost of Sales', fmtCurrency(x.costOfSales)],
        ['Labour Costs', fmtCurrency(x.labour)],
        ['Motor Vehicle', fmtCurrency(x.motorVehicle)],
        ['Net Profit', fmtCurrency(x.turnover - x.totalExpenses)],
        ['Profit Margin', `${((x.turnover - x.totalExpenses) / Math.max(x.turnover, 1) * 100).toFixed(1)}%`],
      ]
    ));
    children.push(new Paragraph({ spacing: { after: 200 } }));
  }

  // Benchmark Comparison
  if (comp?.checks?.length > 0) {
    children.push(createSectionHeading('ATO Benchmark Comparison'));
    children.push(createText(`Industry: ${comp.benchmark?.industry || 'N/A'} | Turnover Range: ${comp.benchmark?.turnoverRange || 'N/A'}`));
    children.push(createTable(
      ['Metric', 'Your Client', 'ATO Benchmark', 'Status'],
      comp.checks.map((c: any) => [c.metric, `${c.clientValue}%`, c.benchmarkRaw, c.status === 'within' ? 'Within Range' : c.status === 'above' ? 'Above Range' : c.status === 'below' ? 'Below Range' : 'Near Boundary'])
    ));
    children.push(new Paragraph({ spacing: { after: 200 } }));
  }

  // Health Score
  if (client?.health_score !== undefined) {
    children.push(createSectionHeading('Client Health Score'));
    children.push(createKeyValue('Overall Score', `${client.health_score}/100 (${client.health_level})`));
    if (client.health_factors) {
      children.push(createTable(
        ['Factor', 'Score', 'Max', 'Detail'],
        client.health_factors.map((f: any) => [f.name, String(f.score), String(f.max), f.detail])
      ));
    }
    children.push(new Paragraph({ spacing: { after: 200 } }));
  }

  children.push(createFooter());
  return new Document({ sections: [{ properties: {}, children }] });
}

async function generateHealthReport(clientId: string, clientName: string): Promise<Document> {
  const [dashRes, compRes, oppRes] = await Promise.all([
    apiClient.get('/firm/dashboard'),
    apiClient.get(`/compliance/check/${clientId}`).catch(() => null),
    apiClient.get('/opportunities/scan').catch(() => null),
  ]);

  const clients = dashRes.data.data?.clients || [];
  const client = clients.find((c: any) => c.id === clientId);
  const clientOpps = (oppRes?.data?.data?.opportunities || []).filter((o: any) => o.clientId === clientId);

  const children: any[] = [
    ...createHeader('Client Health Report', clientName, `Generated: ${new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })}`),
  ];

  // Health Score
  children.push(createSectionHeading('Health Score Analysis'));
  children.push(createKeyValue('Overall Score', `${client?.health_score || 0}/100`));
  children.push(createKeyValue('Level', client?.health_level || 'Unknown'));
  if (client?.health_factors) {
    children.push(createTable(
      ['Factor', 'Score', 'Max', 'Detail'],
      client.health_factors.map((f: any) => [f.name, String(f.score), String(f.max), f.detail])
    ));
  }
  children.push(new Paragraph({ spacing: { after: 200 } }));

  // Opportunities
  if (clientOpps.length > 0) {
    children.push(createSectionHeading('Tax Opportunities'));
    children.push(createTable(
      ['Opportunity', 'Est. Saving', 'Priority', 'Action'],
      clientOpps.map((o: any) => [o.title, fmtCurrency(o.estimatedSaving), o.priority, o.action.substring(0, 60) + '...'])
    ));
    children.push(createKeyValue('Total Potential Savings', fmtCurrency(clientOpps.reduce((s: number, o: any) => s + o.estimatedSaving, 0))));
    children.push(new Paragraph({ spacing: { after: 200 } }));
  }

  // Compliance
  if (compRes?.data?.data?.checks) {
    children.push(createSectionHeading('Compliance Status'));
    const checks = compRes.data.data.checks;
    const withinCount = checks.filter((c: any) => c.status === 'within').length;
    children.push(createKeyValue('Metrics Within Range', `${withinCount}/${checks.length}`));
    const outside = checks.filter((c: any) => c.status !== 'within');
    if (outside.length > 0) {
      children.push(createText('Issues requiring attention:'));
      outside.forEach((c: any) => children.push(createBullet(`${c.metric}: ${c.clientValue}% (benchmark: ${c.benchmarkRaw}) — ${c.status}`)));
    }
  }

  children.push(createFooter());
  return new Document({ sections: [{ properties: {}, children }] });
}

async function generateComplianceReport(clientId: string, clientName: string): Promise<Document> {
  const [compRes, div7aRes, deadRes] = await Promise.all([
    apiClient.get(`/compliance/check/${clientId}`).catch(() => null),
    apiClient.get(`/div7a/check/${clientId}`).catch(() => null),
    apiClient.get('/deadlines').catch(() => null),
  ]);

  const children: any[] = [
    ...createHeader('Compliance Risk Report', clientName, `Generated: ${new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })}`),
  ];

  // Benchmark compliance
  if (compRes?.data?.data?.checks) {
    children.push(createSectionHeading('ATO Benchmark Compliance'));
    children.push(createTable(
      ['Metric', 'Client', 'Benchmark', 'Status', 'Deviation'],
      compRes.data.data.checks.map((c: any) => [c.metric, `${c.clientValue}%`, c.benchmarkRaw, c.status, c.deviation > 0 ? `${c.deviation}%` : 'OK'])
    ));
    children.push(new Paragraph({ spacing: { after: 200 } }));
  }

  // Div 7A
  if (div7aRes?.data?.data) {
    const d = div7aRes.data.data;
    children.push(createSectionHeading('Division 7A Status'));
    if (d.status === 'loans_detected') {
      children.push(createKeyValue('Total Loan Exposure', fmtCurrency(d.totalExposure)));
      children.push(createKeyValue('Benchmark Interest Rate', d.benchmarkRate));
      d.loans?.forEach((l: any) => {
        children.push(createBullet(`${l.account}: ${fmtCurrency(l.absBalance)} (Min repayment: ${fmtCurrency(l.minRepaymentUnsecured)}/yr)`, true));
      });
    } else if (d.status === 'clean') {
      children.push(createText('No director/shareholder loan issues detected.'));
    } else if (d.status === 'not_applicable') {
      children.push(createText(d.message));
    }
    children.push(new Paragraph({ spacing: { after: 200 } }));
  }

  // Deadlines
  const clientDeadlines = (deadRes?.data?.data?.deadlines || []).filter((d: any) => d.clientName === clientName).slice(0, 10);
  if (clientDeadlines.length > 0) {
    children.push(createSectionHeading('Upcoming Deadlines'));
    children.push(createTable(
      ['Deadline', 'Due Date', 'Status', 'Penalty'],
      clientDeadlines.map((d: any) => [d.title, new Date(d.dueDate).toLocaleDateString('en-AU'), d.status, d.penalty?.substring(0, 50) + '...'])
    ));
  }

  children.push(createFooter());
  return new Document({ sections: [{ properties: {}, children }] });
}

async function generateTaxPlanningReport(clientId: string, clientName: string): Promise<Document> {
  // Run all common scenarios
  const scenarioTypes = ['asset_purchase', 'super_contribution', 'prepay_expense', 'vehicle_purchase', 'salary_sacrifice', 'charitable_donation'];
  const res = await apiClient.post('/scenarios/calculate', {
    clientId,
    scenarios: scenarioTypes.map(t => ({ type: t, amount: t === 'vehicle_purchase' ? 50000 : t === 'prepay_expense' ? 30000 : t === 'salary_sacrifice' ? 15000 : 20000 })),
  });

  const data = res.data.data;
  const results = data?.results || [];
  const financials = data?.financials;

  const children: any[] = [
    ...createHeader('Year-End Tax Planning Report', clientName, `FY${financials?.fy || ''} | Generated: ${new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })}`),
  ];

  if (financials) {
    children.push(createSectionHeading('Current Financial Position'));
    children.push(createKeyValue('Turnover', fmtCurrency(financials.currentIncome)));
    children.push(createKeyValue('Total Expenses', fmtCurrency(financials.currentExpenses)));
    children.push(createKeyValue('Taxable Income', fmtCurrency(financials.currentTaxableIncome)));
    children.push(createKeyValue('Entity Type', data.client?.entityType || 'company'));
    children.push(new Paragraph({ spacing: { after: 200 } }));
  }

  if (results.length > 0) {
    children.push(createSectionHeading('Scenario Analysis'));
    children.push(createTable(
      ['Scenario', 'Tax Before', 'Tax After', 'Saving', 'Rate'],
      results.map((r: any) => [r.scenario, fmtCurrency(r.currentTax), fmtCurrency(r.newTax), fmtCurrency(r.taxSaving), `${r.effectiveSavingRate}%`])
    ));
    children.push(new Paragraph({ spacing: { after: 200 } }));

    // Detailed notes for each
    results.forEach((r: any) => {
      children.push(createSectionHeading(r.scenario));
      children.push(createText(r.description));
      r.notes?.forEach((n: string) => children.push(createBullet(n)));
      if (r.warnings?.length > 0) {
        children.push(createText('Warnings:'));
        r.warnings.forEach((w: string) => children.push(createBullet(`⚠ ${w}`)));
      }
      if (r.atoRules?.length > 0) {
        children.push(createText('ATO Rules:'));
        r.atoRules.forEach((a: string) => children.push(createBullet(a)));
      }
      children.push(new Paragraph({ spacing: { after: 100 } }));
    });
  }

  children.push(createFooter());
  return new Document({ sections: [{ properties: {}, children }] });
}

async function generateOpportunityReport(clientId: string, clientName: string): Promise<Document> {
  const res = await apiClient.get('/opportunities/scan');
  const opps = (res.data.data?.opportunities || []).filter((o: any) => o.clientId === clientId);

  const children: any[] = [
    ...createHeader('Tax Opportunity Report', clientName, `Generated: ${new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })}`),
  ];

  children.push(createSectionHeading('Executive Summary'));
  children.push(createKeyValue('Opportunities Found', String(opps.length)));
  children.push(createKeyValue('Total Estimated Savings', fmtCurrency(opps.reduce((s: number, o: any) => s + o.estimatedSaving, 0))));
  children.push(createKeyValue('High Priority', String(opps.filter((o: any) => o.priority === 'high').length)));
  children.push(new Paragraph({ spacing: { after: 200 } }));

  if (opps.length > 0) {
    children.push(createSectionHeading('Opportunities'));
    children.push(createTable(
      ['Category', 'Opportunity', 'Est. Saving', 'Priority'],
      opps.map((o: any) => [o.category, o.title, fmtCurrency(o.estimatedSaving), o.priority])
    ));
    children.push(new Paragraph({ spacing: { after: 200 } }));

    // Details
    opps.forEach((o: any) => {
      children.push(createSectionHeading(`${o.category}: ${o.title}`));
      children.push(createText(o.description));
      children.push(createKeyValue('Estimated Saving', fmtCurrency(o.estimatedSaving)));
      children.push(createKeyValue('Priority', o.priority));
      children.push(createBullet(`Action: ${o.action}`));
      children.push(createBullet(`ATO Rule: ${o.atoRule}`));
      if (o.deadline) children.push(createBullet(`Deadline: ${o.deadline}`));
      children.push(new Paragraph({ spacing: { after: 100 } }));
    });
  } else {
    children.push(createText('No opportunities detected for this client.'));
  }

  children.push(createFooter());
  return new Document({ sections: [{ properties: {}, children }] });
}

// ==================== Quarterly Review ====================

async function generateQuarterlyReport(clientId: string, clientName: string): Promise<Document> {
  const compRes = await apiClient.get(`/compliance/check/${clientId}`).catch(() => null);
  const comp = compRes?.data?.data;

  const children: any[] = [
    ...createHeader('Quarterly Review Report', clientName, `Generated: ${new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })}`),
  ];

  if (comp?.xeroData) {
    const x = comp.xeroData;
    const profit = x.turnover - x.totalExpenses;
    const margin = x.turnover > 0 ? (profit / x.turnover * 100).toFixed(1) : '0';

    children.push(createSectionHeading('Financial Performance'));
    children.push(createTable(
      ['Metric', 'Amount', '% of Revenue'],
      [
        ['Revenue', fmtCurrency(x.turnover), '100%'],
        ['Cost of Sales', fmtCurrency(x.costOfSales), `${(x.costOfSales / Math.max(x.turnover, 1) * 100).toFixed(1)}%`],
        ['Gross Profit', fmtCurrency(x.turnover - x.costOfSales), `${((x.turnover - x.costOfSales) / Math.max(x.turnover, 1) * 100).toFixed(1)}%`],
        ['Labour', fmtCurrency(x.labour), `${(x.labour / Math.max(x.turnover, 1) * 100).toFixed(1)}%`],
        ['Motor Vehicle', fmtCurrency(x.motorVehicle), `${(x.motorVehicle / Math.max(x.turnover, 1) * 100).toFixed(1)}%`],
        ['Total Expenses', fmtCurrency(x.totalExpenses), `${(x.totalExpenses / Math.max(x.turnover, 1) * 100).toFixed(1)}%`],
        ['Net Profit', fmtCurrency(profit), `${margin}%`],
      ]
    ));
    children.push(new Paragraph({ spacing: { after: 200 } }));

    children.push(createSectionHeading('Key Ratios'));
    children.push(createTable(
      ['Ratio', 'Value', 'Assessment'],
      [
        ['Gross Margin', `${((x.turnover - x.costOfSales) / Math.max(x.turnover, 1) * 100).toFixed(1)}%`, parseFloat(margin) > 30 ? 'Strong' : parseFloat(margin) > 15 ? 'Adequate' : 'Needs Review'],
        ['Expense Ratio', `${(x.totalExpenses / Math.max(x.turnover, 1) * 100).toFixed(1)}%`, (x.totalExpenses / Math.max(x.turnover, 1)) < 0.8 ? 'Healthy' : 'High'],
        ['Labour to Revenue', `${(x.labour / Math.max(x.turnover, 1) * 100).toFixed(1)}%`, (x.labour / Math.max(x.turnover, 1)) < 0.3 ? 'Efficient' : 'Review'],
        ['Net Profit Margin', `${margin}%`, parseFloat(margin) > 15 ? 'Strong' : parseFloat(margin) > 5 ? 'Adequate' : 'Low'],
      ]
    ));
    children.push(new Paragraph({ spacing: { after: 200 } }));
  }

  if (comp?.checks?.length > 0) {
    children.push(createSectionHeading('ATO Benchmark Position'));
    children.push(createTable(
      ['Metric', 'Your Client', 'Benchmark Range', 'Status'],
      comp.checks.map((c: any) => [c.metric, `${c.clientValue}%`, c.benchmarkRaw, c.status === 'within' ? 'OK' : c.status.toUpperCase()])
    ));
  }

  children.push(createFooter());
  return new Document({ sections: [{ properties: {}, children }] });
}

// ==================== Year-End Summary ====================

async function generateYearlySummary(clientId: string, clientName: string): Promise<Document> {
  const [compRes, oppRes, div7aRes] = await Promise.all([
    apiClient.get(`/compliance/check/${clientId}`).catch(() => null),
    apiClient.get('/opportunities/scan').catch(() => null),
    apiClient.get(`/div7a/check/${clientId}`).catch(() => null),
  ]);

  const comp = compRes?.data?.data;
  const opps = (oppRes?.data?.data?.opportunities || []).filter((o: any) => o.clientId === clientId);
  const div7a = div7aRes?.data?.data;

  const children: any[] = [
    ...createHeader('Annual Financial Summary', clientName, `Generated: ${new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })}`),
  ];

  // Income Statement
  if (comp?.xeroData) {
    const x = comp.xeroData;
    const profit = x.turnover - x.totalExpenses;
    children.push(createSectionHeading('Income Statement Summary'));
    children.push(createTable(
      ['Item', 'Amount'],
      [
        ['Total Revenue', fmtCurrency(x.turnover)],
        ['Cost of Sales', fmtCurrency(x.costOfSales)],
        ['Gross Profit', fmtCurrency(x.turnover - x.costOfSales)],
        ['Operating Expenses', fmtCurrency(x.totalExpenses - x.costOfSales)],
        ['Total Expenses', fmtCurrency(x.totalExpenses)],
        ['Net Profit Before Tax', fmtCurrency(profit)],
      ]
    ));
    children.push(new Paragraph({ spacing: { after: 200 } }));
  }

  // Benchmark
  if (comp?.checks?.length > 0) {
    children.push(createSectionHeading('Industry Benchmark Comparison'));
    children.push(createText(`Industry: ${comp.benchmark?.industry || 'N/A'} | Turnover Range: ${comp.benchmark?.turnoverRange || 'N/A'}`));
    children.push(createTable(
      ['Metric', 'Client', 'Benchmark', 'Status'],
      comp.checks.map((c: any) => [c.metric, `${c.clientValue}%`, c.benchmarkRaw, c.status === 'within' ? 'Within Range' : c.status])
    ));
    children.push(new Paragraph({ spacing: { after: 200 } }));
  }

  // Div 7A
  if (div7a?.status === 'loans_detected') {
    children.push(createSectionHeading('Division 7A Status'));
    children.push(createKeyValue('Total Loan Exposure', fmtCurrency(div7a.totalExposure)));
    div7a.loans?.forEach((l: any) => {
      children.push(createBullet(`${l.account}: ${fmtCurrency(l.absBalance)} — Min repayment: ${fmtCurrency(l.minRepaymentUnsecured)}/yr`));
    });
    children.push(new Paragraph({ spacing: { after: 200 } }));
  }

  // Opportunities
  if (opps.length > 0) {
    children.push(createSectionHeading('Tax Opportunities Identified'));
    children.push(createKeyValue('Total Potential Savings', fmtCurrency(opps.reduce((s: number, o: any) => s + o.estimatedSaving, 0))));
    children.push(createTable(
      ['Opportunity', 'Category', 'Est. Saving', 'Priority'],
      opps.map((o: any) => [o.title, o.category, fmtCurrency(o.estimatedSaving), o.priority])
    ));
  }

  children.push(createFooter());
  return new Document({ sections: [{ properties: {}, children }] });
}

// ==================== BAS Reconciliation ====================

async function generateBASReport(clientId: string, clientName: string): Promise<Document> {
  const basRes = await apiClient.get(`/bas/validate/${clientId}`).catch(() => null);
  const data = basRes?.data?.data;

  const children: any[] = [
    ...createHeader('BAS Reconciliation Report', clientName, `Generated: ${new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })}`),
  ];

  if (data?.gst) {
    children.push(createSectionHeading('GST Summary'));
    children.push(createText(`Period: ${data.period?.from} to ${data.period?.to} (${data.previousQuarter})`));
    children.push(createTable(
      ['Item', 'Amount'],
      [
        ['Total Sales (inc GST)', fmtCurrency(data.gst.totalSales)],
        ['GST Collected on Sales', fmtCurrency(data.gst.gstCollected)],
        ['Total Purchases (inc GST)', fmtCurrency(data.gst.totalPurchases)],
        ['GST Paid on Purchases', fmtCurrency(data.gst.gstPaid)],
        ['Net GST Payable', fmtCurrency(data.gst.netGST)],
      ]
    ));
    children.push(new Paragraph({ spacing: { after: 200 } }));

    if (data.bas) {
      children.push(createSectionHeading('Estimated BAS Labels'));
      children.push(createTable(
        ['Label', 'Description', 'Amount'],
        [
          ['1A', 'Total sales (including GST)', fmtCurrency(data.bas.label1a)],
          ['1B', 'GST on sales', fmtCurrency(data.bas.label1b)],
          ['11', 'Total purchases (including GST)', fmtCurrency(data.bas.label11)],
          ['11B', 'GST on purchases (input tax credits)', fmtCurrency(data.bas.label11b)],
          ['9', 'Net GST payable / (refundable)', fmtCurrency(data.bas.label9)],
        ]
      ));
      children.push(new Paragraph({ spacing: { after: 200 } }));
    }

    if (data.issues?.length > 0) {
      children.push(createSectionHeading('Validation Results'));
      data.issues.forEach((issue: any) => {
        children.push(createBullet(`[${issue.type.toUpperCase()}] ${issue.title}: ${issue.detail}`));
      });
    }
  } else {
    children.push(createText(data?.message || 'Could not retrieve BAS data. Ensure client is connected to Xero.'));
  }

  children.push(createFooter());
  return new Document({ sections: [{ properties: {}, children }] });
}

// ==================== Page Component ====================

export default function ClientReportsPage() {
  const [selectedClientId, setSelectedClientId] = useState('');
  const [generatingType, setGeneratingType] = useState<string | null>(null);

  const { data: clientsData } = useQuery({
    queryKey: ['clients-for-reports'],
    queryFn: async () => { const r = await apiClient.get('/clients?limit=500'); return r.data.data; },
  });
  const clients = clientsData?.clients || clientsData || [];
  const selectedClient = clients.find((c: any) => c.id === selectedClientId);
  const clientName = selectedClient?.displayName || selectedClient?.display_name || 'Client';

  const generateReport = async (type: string) => {
    if (!selectedClientId) { toast.error('Select a client first'); return; }
    setGeneratingType(type);
    try {
      let doc: Document;
      switch (type) {
        case 'monthly': doc = await generateMonthlyReport(selectedClientId, clientName); break;
        case 'quarterly': doc = await generateQuarterlyReport(selectedClientId, clientName); break;
        case 'yearly': doc = await generateYearlySummary(selectedClientId, clientName); break;
        case 'bas': doc = await generateBASReport(selectedClientId, clientName); break;
        case 'health': doc = await generateHealthReport(selectedClientId, clientName); break;
        case 'compliance': doc = await generateComplianceReport(selectedClientId, clientName); break;
        case 'tax_planning': doc = await generateTaxPlanningReport(selectedClientId, clientName); break;
        case 'opportunity': doc = await generateOpportunityReport(selectedClientId, clientName); break;
        default: throw new Error('Unknown report type');
      }
      const blob = await Packer.toBlob(doc);
      const filename = `${clientName.replace(/[^a-zA-Z0-9]/g, '_')}_${type}_${new Date().toISOString().split('T')[0]}.docx`;
      saveAs(blob, filename);
      toast.success('Report downloaded');
    } catch (err: any) {
      toast.error(err.message || 'Failed to generate report');
    } finally {
      setGeneratingType(null);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        icon={FileText}
        title="Client Reports"
        description="Generate branded Word reports from client data"
      />


      <Card>
        <CardContent className="pt-4 pb-4">
          <div className="flex items-center gap-4">
            <label className="text-sm font-medium whitespace-nowrap">Select Client:</label>
            <Select value={selectedClientId} onValueChange={setSelectedClientId}>
              <SelectTrigger className="w-[350px]">
                <SelectValue placeholder="Choose a client..." />
              </SelectTrigger>
              <SelectContent className="max-h-[300px]">
                {clients.map((c: any) => (
                  <SelectItem key={c.id} value={c.id}>{c.displayName || c.display_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {selectedClientId && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {REPORT_TYPES.map(rt => {
            const Icon = rt.icon;
            const isGenerating = generatingType === rt.id;
            return (
              <Card key={rt.id} className="hover:shadow-md transition-shadow">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Icon className="h-4 w-4 text-primary" />
                    {rt.label}
                  </CardTitle>
                  <CardDescription className="text-xs">{rt.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <Button size="sm" className="w-full gap-2" onClick={() => generateReport(rt.id)} disabled={!!generatingType}>
                    {isGenerating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
                    {isGenerating ? 'Generating...' : 'Download Word'}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
